///
/// EarthBrowser
///
/// Created by Matt Giger on April 6, 2026
/// Copyright (c) 2026 Geodata Labs
///

import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Landmark } from 'lucide-react'
import { LocationBrowser, type LocationData } from '@/components/features/location-browser/LocationBrowser'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { Database } from '@/earthbrowser/database/Database'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'
import { useAppDispatch } from '@/redux/store'
import { isMobile } from '@/earthbrowser/utils/platform'
import { useSearchParams } from 'react-router-dom'

export const LOCATION_BROWSER_WINDOW_ID = 'location-browser'

interface LocationBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const LocationBrowserRenderer = memo(function LocationBrowserRenderer({
	windowId,
	windowState,
}: LocationBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const [, setSearchParams] = useSearchParams()
	const database = Database.getDb()

	const [countries, setCountries] = useState<Record<string, string>>({})
	const [admins, setAdmins] = useState<Record<string, Record<string, string>>>({})

	useEffect(() => {
		async function loadCountriesAndAdmins() {
			if (!database) {
				return
			}

			try {
				const localAdmins: Record<string, Record<string, string>> = {}
				const cnt = await database.exec("SELECT country_code, name FROM countries", [], "critical") as { country_code: string, name: string }[]
				const countriesMap = cnt.reduce((acc, country) => ({ ...acc, [country.country_code]: country.name }), {})
				const adm = await database.exec("SELECT country_code, admin_code, name FROM country_admin", [], "critical") as { country_code: string, admin_code: string, name: string }[]
				for (const admin of adm) {
					if (!localAdmins[admin.country_code]) {
						localAdmins[admin.country_code] = {}
					}
					localAdmins[admin.country_code][admin.admin_code] = admin.name
				}
				setCountries(countriesMap)
				setAdmins(localAdmins)
			} catch (error) {
				console.error('[LocationBrowserRenderer] Failed to load countries/admins:', error)
			}
		}

		void loadCountriesAndAdmins()
	}, [database])

	const handleLocationSelect = useCallback((location: LocationData) => {
		dispatch(sendDatasetAction({ type: "location", action: "selectId", id: location.geoid.toString() }))
		setSearchParams(prev => {
			const newParams = new URLSearchParams(prev)
			newParams.set("target", `geoid-${location.geoid}`)
			return newParams
		})

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [dispatch, setSearchParams, windowId])

	const content = useMemo(() => (
		<LocationBrowser
			onLocationSelect={handleLocationSelect}
			database={database}
			countries={countries}
			admins={admins}
			embeddedInWindow
		/>
	), [handleLocationSelect, database, countries, admins])

	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Landmark className="w-5 h-5" />}
			iconColor="text-blue-400"
		>
			{content}
		</DatasetBrowserWindow>
	)
})
